import * as React from "react";
import {
  createFileRoute,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import { auth } from "../config/firebase";
import OnboardingModal from "../components/OnboardingModal.tsx";
import { Vibe, Scene, RetailerProfile, BrandIdentity } from "../types.ts";
import { createBrandTemplate } from "../services/brandTemplateService";
import {
  trackCreationGoalSelected,
  trackOnboardingCompleted,
  trackProjectCreated,
  trackQuickstartStarted,
} from "../services/analyticsService";
import { findCreationEntryById } from "../config/creationCatalog.ts";
import { flowForGoal } from "../utils/quickStartFlow.ts";
import {
  useCreateProject,
  useListBrandTemplates,
} from "@dataconnect/generated/react";
import { useQuickStartTour } from "../hooks/useQuickStartTour";
import { furnitureVibes, plantVibes } from "../data/presetVibes";
import { furnitureScenes, plantScenes } from "../data/presetScenes";

const OnboardingPage: React.FC = () => {
  const navigate = useNavigate();
  const search = useSearch({ from: "/onboarding" });
  const user = auth.currentUser;
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const { mutateAsync: createProject } = useCreateProject();
  const { data: brandsData, refetch: refetchBrands } = useListBrandTemplates({
    enabled: !!user,
  });
  // Display mode is driven by the route intent so it's stable while the
  // brand list query is still loading. The close button is gated separately
  // on the loaded brand count, since closing is only safe once we know at
  // least one brand already exists.
  const isAddBrandRoute = search.returnTab === "library";
  const hasExistingBrands = (brandsData?.brandTemplates.length ?? 0) > 0;

  // Memoize the saved brand across retries: if createProject (or refetch)
  // fails after the brand template has been persisted, a re-submit must
  // not create a second BrandTemplate row.
  const savedBrandRef = React.useRef<BrandIdentity | null>(null);

  const { start: startQuickStartTour } = useQuickStartTour();

  const handleOnboardingComplete = async (data: {
    vibes: Vibe[];
    scenes: Scene[];
    profiles: RetailerProfile[];
    projectName?: string;
    goal?: string;
  }) => {
    try {
      setSaving(true);
      setSaveError(null);

      let savedBrand = savedBrandRef.current;
      if (!savedBrand) {
        const newBrand: BrandIdentity = {
          id: crypto.randomUUID(),
          name: data.profiles[0]?.name || "New Brand Identity",
          context: data.profiles[0]?.context || "",
          vibes: data.vibes,
          scenes: data.scenes,
        };
        savedBrand = await createBrandTemplate(newBrand);
        savedBrandRef.current = savedBrand;
        trackOnboardingCompleted();
      }
      await refetchBrands();

      if (data.projectName) {
        const result = await createProject({
          name: data.projectName,
          brandTemplateId: savedBrand.id,
        });
        const newProjectId = result.project_insert.id;
        trackProjectCreated(newProjectId);
        // A chosen goal starts its guided tour. "Just look around" starts
        // none: the user asked to explore, and a tour on top of the empty
        // project's cards would only add noise (SIS-566).
        const goal = data.goal ? findCreationEntryById(data.goal) : undefined;
        if (goal) {
          const flow = flowForGoal(goal);
          startQuickStartTour(flow);
          trackQuickstartStarted(flow);
          trackCreationGoalSelected({
            goalId: goal.id,
            surface: "onboarding",
            projectId: newProjectId,
          });
        }
        // Straight into the chosen destination (SIS-565/SIS-566): the
        // dashboard detour only cost a click, and it keeps its highlighted
        // card for users who leave the tour and come back later. Studio
        // goals travel as ?mode= because the image type lives in the
        // project's studio state, which this route cannot reach.
        const params = { projectId: newProjectId };
        if (goal?.surface === "highlights") {
          navigate({ to: "/projects/$projectId/highlights", params });
        } else if (goal?.surface === "video") {
          navigate({ to: "/projects/$projectId/video", params });
        } else {
          navigate({
            to: "/projects/$projectId",
            params,
            search: goal ? { mode: goal.imageType } : {},
          });
        }
        return;
      }

      navigate({
        to: "/",
        search: search.returnTab
          ? { returnTab: search.returnTab }
          : { returnTab: undefined },
      });
    } catch (error) {
      console.error("Failed to complete onboarding:", error);
      setSaveError("Failed to complete setup. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (!user) {
    return null; // Redirect to login handled by __root.tsx
  }

  const handleClose = () => {
    navigate({
      to: "/",
      search: search.returnTab
        ? { returnTab: search.returnTab }
        : { returnTab: undefined },
    });
  };

  return (
    <>
      {saving && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50">
          <div className="rounded-xl bg-white p-6 shadow-xl">
            <div className="flex items-center gap-3">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
              <span className="text-lg">Setting up your project space...</span>
            </div>
          </div>
        </div>
      )}
      {saveError && (
        <div className="fixed top-4 right-4 z-50 max-w-md rounded-xl bg-red-50 p-4 shadow-xl">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-red-600"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-red-800">Error</p>
              <p className="mt-1 text-sm text-red-700">{saveError}</p>
            </div>
            <button
              onClick={() => setSaveError(null)}
              className="flex-shrink-0 text-red-400 hover:text-red-600"
            >
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
        </div>
      )}
      <OnboardingModal
        onComplete={handleOnboardingComplete}
        onClose={isAddBrandRoute && hasExistingBrands ? handleClose : undefined}
        furniturePreset={{ vibes: furnitureVibes, scenes: furnitureScenes }}
        plasticPlantsPreset={{ vibes: plantVibes, scenes: plantScenes }}
        isAddingBrand={isAddBrandRoute}
      />
    </>
  );
};

export const Route = createFileRoute("/onboarding")({
  component: OnboardingPage,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      returnTab:
        (search.returnTab as "projects" | "library" | undefined) || undefined,
    };
  },
});
